import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabs } from './TabsContext';
import { isDialogOpen } from './dialogGuard';

export interface TabSwitcherState {
  open: boolean;
  /** Keys in MRU order, snapshotted at open time (stable while showing). */
  orderedKeys: string[];
  selectedKey: string | null;
  /** Mouse hover/click selection. */
  select: (key: string) => void;
  /** Click / outside-click / Escape — cancels without activating. */
  cancel: () => void;
  /** Mouse click on an item — activates it immediately (same navigation
   *  path as Alt-release) and closes the popover. */
  activateAndClose: (key: string) => void;
}

/**
 * Alt+Q MRU tab switcher (plan §3.5). The physical key (`e.code === 'KeyQ'`)
 * is used so it fires regardless of OS keyboard language. Holding Alt and
 * tapping Q opens a popover pre-selecting the PREVIOUS tab (mru[1], or
 * mru[0] if there's only one open tab); each further Q while Alt stays held
 * advances the selection with wrap-around; releasing Alt activates the
 * current selection and closes the popover. Escape, a window blur, or an
 * outside click (handled by the consuming component) cancel without
 * activating. ArrowUp/ArrowDown move the selection too.
 *
 * Deliberately has NO "isTypingTarget" guard (unlike the g/e sidebar-search
 * shortcut in AppShell) — Alt+Q isn't used by any `<input>` or by AgGrid's
 * cell editors, so it must keep working while focus sits inside either. The
 * single guard is an open modal dialog ([role="dialog"]), so the switcher
 * never fights a dialog for the Escape key or steals its focus.
 *
 * No Shift+Tab (plan D2 — conflicts with AgGrid's cell-to-cell Tab
 * navigation while editing).
 */
export function useTabSwitcher(): TabSwitcherState {
  const { tabs, mru } = useTabs();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Refs mirroring the latest live values so the single keydown/keyup/blur
  // listener set up once below doesn't need to be torn down and re-attached
  // on every tabs/mru change (which happens on every navigation).
  const openRef = useRef(open);
  openRef.current = open;
  const selectedKeyRef = useRef(selectedKey);
  selectedKeyRef.current = selectedKey;
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const mruRef = useRef(mru);
  mruRef.current = mru;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // Snapshot of the MRU order at open time — see TabSwitcherState.orderedKeys.
  const orderRef = useRef<string[]>([]);

  // Activation goes through the router — same `navigate(tab.location,
  // {replace:true, state:{lopTabActivate:true}})` pattern LopTabBar uses, NOT
  // the raw TabsContext `activate` (which only flips activeKey/mru and would
  // leave the browser URL pointing at the tab we're switching AWAY from).
  // TabRouterSync recognizes the `lopTabActivate` flag as pure focus.
  const activateByKey = useCallback((key: string) => {
    const tab = tabsRef.current.find((t) => t.key === key);
    if (tab) navigateRef.current(tab.location, { replace: true, state: { lopTabActivate: true } });
  }, []);

  const closeSwitcher = useCallback(() => {
    setOpen(false);
    setSelectedKey(null);
  }, []);

  const select = useCallback((key: string) => setSelectedKey(key), []);
  const cancel = useCallback(() => closeSwitcher(), [closeSwitcher]);

  const openSwitcher = useCallback(() => {
    const order = mruRef.current.filter((k) => tabsRef.current.some((t) => t.key === k));
    if (order.length === 0) return;
    orderRef.current = order;
    setSelectedKey(order.length > 1 ? order[1] : order[0]);
    setOpen(true);
  }, []);

  const moveSelection = useCallback((delta: number) => {
    const order = orderRef.current;
    if (order.length === 0) return;
    setSelectedKey((prev) => {
      const idx = prev ? order.indexOf(prev) : -1;
      const next = (idx + delta + order.length) % order.length;
      return order[next];
    });
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.code === 'KeyQ' && e.altKey) {
        if (isDialogOpen()) return;
        e.preventDefault(); // also stops Alt+Q's browser-menu side effect
        if (!openRef.current) {
          openSwitcher();
        } else {
          moveSelection(1);
        }
        return;
      }
      if (!openRef.current) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeSwitcher();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveSelection(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveSelection(-1);
      }
    }

    function onKeyUp(e: KeyboardEvent) {
      if (e.key !== 'Alt') return;
      if (!openRef.current) return;
      const key = selectedKeyRef.current;
      closeSwitcher();
      if (key) activateByKey(key);
    }

    function onWindowBlur() {
      if (openRef.current) closeSwitcher();
    }

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [openSwitcher, moveSelection, closeSwitcher, activateByKey]);

  const activateAndClose = useCallback(
    (key: string) => {
      closeSwitcher();
      activateByKey(key);
    },
    [closeSwitcher, activateByKey],
  );

  return { open, orderedKeys: orderRef.current, selectedKey, select, cancel, activateAndClose };
}
